// ItemKindForm — add an item kind, from wherever the person happens to be.
//
// It is mounted twice: by the Item Kinds admin page behind "+ New Kind", and by
// the SKU builder behind the "New type…" option in its Board picker. That second
// one is the point. Per the standing rule in web/CLAUDE.md, everything the
// current task needs is reachable from where the person stands: somebody
// cataloging a board that has no kind yet should not abandon a half-filled item
// form to visit an admin page and come back. The kind lands straight back in the
// field that asked for it.
//
// So the form is a component, not a page's private modal, and it hands the
// created row to its caller rather than deciding what happens next.

import React, { useState } from 'react';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useItemKindsStore, { CodeMode, ItemKind, normalizeKind } from '../stores/useItemKindsStore';
import { exampleCode } from '../Parts/skuGrammar';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Alert } from '../components/ui/Alert';
import Dialog from '../components/ui/Dialog';
import { Field } from '../components/ui/Field';
import SegTrack from '../components/ui/SegTrack';
import Reveal from '../components/ui/Reveal';

/** The grammar the server enforces: 2 to 4 characters, a letter first. */
export const ITEM_KIND_KEY_REGEX = /^[A-Z][A-Z0-9]{1,3}$/;
/** The stricter shape a BOARD code must have: the SKU grammar reads 2 or 3 letters. */
export const STRUCTURED_KEY_REGEX = /^[A-Z]{2,3}$/;

/** A name to a key: the first three characters that could be part of one. */
export function keyFromLabel(label: string): string {
  return String(label || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase();
}

/** Lowercase, spaces and dashes gone: "IIS2 MDC" and "iis2-mdc" compare equal. */
const squash = (s: string): string => String(s || '').toLowerCase().replace(/[\s-]/g, '');

/**
 * Does this name read like a part number rather than a category?
 *
 * Seven kinds were created in two days on production with names like
 * STM32H743IIK6TR, because the person had a part number in front of them and
 * the only empty field on screen was this one. A kind is a category (Camera,
 * MCU, Cable); the part number belongs on the item as its MPN.
 *
 * Two tests, either one is enough:
 *  - a whitespace-separated token carrying letters AND two or more digits, which
 *    is what a manufacturer code looks like and what "Gen2 ESC" or "M3 screw"
 *    deliberately is not;
 *  - the name containing, or contained in, the part number the calling surface
 *    already knows (IIS2MDC against an MPN of IIS2MDC-TR), which catches the
 *    codes the heuristic cannot see.
 */
export function looksLikePartNumber(label: string, hint?: string | null): boolean {
  const text = String(label || '').trim();
  if (!text) return false;
  const hinted = squash(hint);
  const squashed = squash(text);
  if (hinted.length >= 4 && (squashed.includes(hinted) || hinted.includes(squashed)) && squashed.length >= 4) return true;
  return text
    .split(/\s+/)
    .some((token) => /[A-Za-z]/.test(token) && (token.match(/\d/g) || []).length >= 2);
}

const MODES: { value: CodeMode; label: string; blurb: string }[] = [
  { value: 'structured', label: 'Board code', blurb: 'A segment in a board SKU. Shows up in the SKU builder.' },
  { value: 'short', label: 'Short code', blurb: 'The stem of a short code for everything that is not a board.' },
];

/**
 * The two-state code-style control, shared with the admin table's row cells.
 *
 * Buttons with aria-pressed rather than radios: it also lives inside a table
 * row, where a form control group would need a unique name per row and would
 * announce as a form nobody filled in.
 */
export const CodeModeSegmented: React.FC<{
  value: CodeMode;
  onChange: (next: CodeMode) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Names the group. In a table, say which row it belongs to. */
  ariaLabel?: string;
}> = ({ value, onChange, disabled, size = 'md', ariaLabel = 'Code style' }) => (
  <SegTrack
    label={ariaLabel}
    size={size}
    value={value}
    onChange={(k) => onChange(k as CodeMode)}
    options={MODES.map((m) => ({ key: m.value, label: m.label, disabled }))}
  />
);

interface Props {
  onClose: () => void;
  /** The created kind, already in the store. Select it, or just close. */
  onCreated: (kind: ItemKind) => void;
  /** Which code style the calling surface needs. The SKU builder needs boards. */
  defaultMode?: CodeMode;
  /**
   * Fix the code style to `defaultMode`. The SKU builder's Board picker opens
   * this form for a BOARD type: a kind created as a short code from there could
   * not be selected in that picker, so the choice is not offered rather than
   * offered and then refused.
   */
  lockMode?: boolean;
  /**
   * The part number the calling surface is looking at, if it has one. A name
   * equal to it is the mistake this form exists to catch, and no heuristic can
   * see it from the name alone.
   */
  partNumberHint?: string | null;
}

const ItemKindForm: React.FC<Props> = ({
  onClose,
  onCreated,
  defaultMode = 'structured',
  lockMode = false,
  partNumberHint = null,
}) => {
  const api = useApi();
  const toast = useToast();
  const upsert = useItemKindsStore((s) => s.upsert);

  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [codeMode, setCodeMode] = useState<CodeMode>(defaultMode);
  const [tracked, setTracked] = useState(false);
  const [sortOrder, setSortOrder] = useState('100');
  const [submitting, setSubmitting] = useState(false);
  const [partNumberAck, setPartNumberAck] = useState(false);

  // The key follows the name until somebody edits it. Then it is theirs, and it
  // stays theirs even if the name changes afterwards.
  const effectiveKey = keyTouched ? key : keyFromLabel(label);
  // A board code is what the board SKU grammar reads as its second segment: 2 or
  // 3 letters. The server refuses a structured kind outside that shape, so the
  // hint says it before the request does.
  const keyValid = codeMode === 'structured'
    ? STRUCTURED_KEY_REGEX.test(effectiveKey)
    : ITEM_KIND_KEY_REGEX.test(effectiveKey);
  // The acknowledgement is for the text they had in front of them, so any edit
  // to the name drops it (see the Name field's onChange) and this recomputes.
  const needsPartNumberAck = looksLikePartNumber(label, partNumberHint);
  const dirty =
    label !== '' || key !== '' || codeMode !== defaultMode || tracked || sortOrder !== '100' || partNumberAck;
  const canSubmit = !!label.trim() && keyValid && !submitting && (!needsPartNumberAck || partNumberAck);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const created = await api.post('/item-kinds', {
        body: {
          key: effectiveKey,
          label: label.trim(),
          code_mode: codeMode,
          tracked_per_instance_default: tracked,
          sort_order: Number(sortOrder) || 100,
        },
      });
      // The row may arrive bare or wrapped; either way it is normalized once.
      const kind = normalizeKind((created as any)?.kind ?? created);
      upsert(kind);
      toast.success(`Added ${kind.label || label.trim()}`);
      onCreated(kind);
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'Could not add that kind');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="New item kind"
      size="md"
      dismissible={false}
      dirty={dirty}
      busy={submitting}
      onSubmit={() => { if (canSubmit) submit(); }}
      footer={
        <>
          <button type="button" onClick={onClose} className={buttonClass('quiet')}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit} className={buttonClass('filled')}>
            {submitting ? 'Adding…' : 'Add kind'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block space-y-1">
          <span className="text-xs font-mono uppercase tracking-wider text-on-surface-variant">Name</span>
          <Field
            autoFocus
            value={label}
            placeholder="Pyro board"
            onChange={(e) => {
              setLabel(e.target.value);
              setPartNumberAck(false);
            }}
          />
        </label>
        <p className="-mt-2 text-xs text-on-surface-variant">
          A kind is a category, like Camera, MCU or Cable, not a part number. The part number
          goes on the item.
        </p>

        <Reveal show={needsPartNumberAck}>
          <Alert tone="warning" live="alert">
            <p className="m-0">That reads like a part number. A kind is the category it belongs to.</p>
            <Checkbox
              size="sm"
              checked={partNumberAck}
              onChange={(e) => setPartNumberAck(e.target.checked)}
              label="This is not a part number"
            />
          </Alert>
        </Reveal>

        <label className="block space-y-1">
          <span className="text-xs font-mono uppercase tracking-wider text-on-surface-variant">Key</span>
          <Field
            className="font-mono uppercase"
            warn={!!effectiveKey && !keyValid}
            value={effectiveKey}
            maxLength={4}
            placeholder="PYR"
            onChange={(e) => {
              setKeyTouched(true);
              setKey(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4));
            }}
          />
        </label>
        <p className={`-mt-2 text-xs ${effectiveKey && !keyValid ? 'text-amber-400/90' : 'text-on-surface-variant'}`}>
          {codeMode === 'structured'
            ? 'Two or three letters, like PYR or AP. That is what a board SKU can carry. It never changes after this.'
            : 'Two to four characters, starting with a letter. It never changes after this.'}
        </p>

        <div className="space-y-1">
          <span className="text-xs font-mono uppercase tracking-wider text-on-surface-variant">Code style</span>
          <div className="flex items-center gap-3 flex-wrap">
            <CodeModeSegmented value={codeMode} onChange={setCodeMode} disabled={submitting || lockMode} />
            <span className="font-mono text-xs text-on-surface-variant">
              {exampleCode(effectiveKey || 'PYR', codeMode)}
            </span>
      </div>
      <p className="text-xs text-on-surface-variant">
        {MODES.find((m) => m.value === codeMode)?.blurb}
      </p>
    </div>

    <Checkbox
      checked={tracked}
      onChange={(e) => setTracked(e.target.checked)}
      label="Tracked per unit"
      description="New items of this kind get a record per physical unit. Set once here, changeable per item."
    />

    <label className="block space-y-1">
      <span className="text-xs font-mono uppercase tracking-wider text-on-surface-variant">Order</span>
      <Field
        type="number"
        value={sortOrder}
        onChange={(e) => setSortOrder(e.target.value)}
      />
    </label>
      </div>
    </Dialog>
  );
};

export default ItemKindForm;
